import { nanoid } from "nanoid";

export const RAD_ACTION_KINDS = [
  "action",
  "activity",
  "interaction",
  "external_event",
  "external",
  "trigger",
  "start-role",
  "end-role",
  "case",
  "part",
  "repeat",
  "ellipsis",
  "sync",
  "void",
  "refinement",
];

export const hasVnextActivityShape = (rad) =>
  Array.isArray(rad?.activities) &&
  rad.activities.some(
    (activity) =>
      activity &&
      typeof activity === "object" &&
      ("roleId" in activity || "threadId" in activity || "pre" in activity || "post" in activity)
  );

export const isVnextModel = (rad) =>
  rad?.modelVersion === "vnext" ||
  hasVnextActivityShape(rad) ||
  (Array.isArray(rad?.actions) && !Array.isArray(rad?.caseRefinements));

export const ensureVnextArrays = (rad) => ({
  ...rad,
  roles: Array.isArray(rad?.roles) ? rad.roles : [],
  threads: Array.isArray(rad?.threads) ? rad.threads : [],
  states: Array.isArray(rad?.states) ? rad.states : [],
  activities: Array.isArray(rad?.activities)
    ? rad.activities
    : Array.isArray(rad?.actions)
      ? rad.actions
      : [],
  connections: Array.isArray(rad?.connections) ? rad.connections : [],
  info:
    rad && typeof rad.info === "object" && rad.info !== null
      ? {
          ...rad.info,
          ...(typeof rad.info.targetEngine === "string"
            ? {}
            : { targetEngine: "reachability" }),
        }
      : { targetEngine: "reachability" },
  version: typeof rad?.version === "number" ? rad.version : 0,
  modelVersion: "vnext",
});

const bump = (rad) => ({ ...rad, version: (rad.version ?? 0) + 1 });

const normalizeActionKind = (type, className) => {
  if (type === "externalEvent") return "external_event";
  if (RAD_ACTION_KINDS.includes(type)) {
    return type;
  }
  if (className === "refinement") return "refinement";
  return "activity";
};

export const validateVnext = (rad) => {
  const next = ensureVnextArrays(rad);
  const roleIds = new Set(next.roles.map((r) => r.id));
  const threadById = new Map(next.threads.map((t) => [t.id, t]));
  const stateById = new Map(next.states.map((s) => [s.id, s]));
  next.threads.forEach((thread) => {
    if (!roleIds.has(thread.roleId)) {
      throw new Error(`thread ${thread.id} references missing role ${thread.roleId}`);
    }
  });
  next.states.forEach((state) => {
    const thread = threadById.get(state.threadId);
    if (!thread) throw new Error(`state ${state.id} references missing thread ${state.threadId}`);
    if (thread.roleId !== state.roleId) throw new Error(`state ${state.id} role/thread mismatch`);
  });
  next.activities.forEach((activity) => {
    const thread = threadById.get(activity.threadId);
    if (!thread) throw new Error(`activity ${activity.id} missing thread ${activity.threadId}`);
    if (!roleIds.has(activity.roleId)) throw new Error(`activity ${activity.id} missing role ${activity.roleId}`);
    const pre = stateById.get(activity.pre);
    const post = stateById.get(activity.post);
    if (!pre || !post) throw new Error(`activity ${activity.id} missing state`);
  });
  next.connections.forEach((connection) => {
    if (!Array.isArray(connection.interactionIds) || connection.interactionIds.length < 2) {
      throw new Error(`connection ${connection.id} must include at least two interactions`);
    }
  });
  return true;
};

export const addRoleVnext = (rad, payload) => {
  const next = ensureVnextArrays(rad);
  if (!payload?.label) throw new Error("label is required");
  const roleId = payload.id || nanoid();
  if (next.roles.some((role) => role.id === roleId)) {
    throw new Error(`role ${roleId} already exists`);
  }
  return bump({
    ...next,
    roles: [
      ...next.roles,
      {
        id: roleId,
        label: payload.label,
        transient: payload.transient,
        multiple: payload.multiple,
        position: payload.position,
        size: payload.size,
      },
    ],
  });
};

export const addThreadVnext = (rad, payload) => {
  const next = ensureVnextArrays(rad);
  const roleId = payload.role_id;
  if (!roleId) throw new Error("role_id required");
  const role = next.roles.find((candidate) => candidate.id === roleId);
  if (!role) throw new Error(`role ${roleId} not found`);
  const threadId = payload.id || nanoid();
  const startId = payload.prestate_id || nanoid();
  const endId = payload.poststate_id || nanoid();
  const states = [...next.states];
  if (!states.some((state) => state.id === startId)) {
    states.push({
      id: startId,
      roleId,
      threadId,
      label: "START",
      flags: { start: true },
      show: false,
    });
  }
  if (!states.some((state) => state.id === endId)) {
    states.push({
      id: endId,
      roleId,
      threadId,
      label: "END",
      flags: { end: true },
      show: false,
    });
  }
  return bump({
    ...next,
    states,
    threads: [
      ...next.threads,
      {
        id: threadId,
        roleId,
        class: payload.className || "thread",
        label: payload.label,
      },
    ],
  });
};

export const addActivityToThreadVnext = (rad, payload, threadId) => {
  const next = ensureVnextArrays(rad);
  const thread = next.threads.find((candidate) => candidate.id === threadId);
  if (!thread) throw new Error(`thread ${threadId} not found`);

  let pre = payload.prestate_id;
  let post = payload.poststate_id;
  let states = next.states;
  const activitiesInThread = next.activities.filter((activity) => activity.threadId === threadId);

  if (!pre || !post) {
    const startState = states.find((state) => state.threadId === threadId && state.flags?.start);
    const endState = states.find((state) => state.threadId === threadId && state.flags?.end);
    if (!startState || !endState) {
      throw new Error(`thread ${threadId} missing start/end states`);
    }
    if (activitiesInThread.length === 0) {
      pre = startState.id;
      post = endState.id;
    } else {
      const predecessor = activitiesInThread.find((activity) => activity.post === endState.id);
      if (!predecessor) {
        throw new Error("vnext append requires --pre and --post when thread end cannot be inferred");
      }
      const bridgeStateId = nanoid();
      states = [
        ...states,
        {
          id: bridgeStateId,
          roleId: thread.roleId,
          threadId,
          label: "",
          show: false,
        },
      ];
      const activitiesRebound = next.activities.map((activity) =>
        activity.id === predecessor.id ? { ...activity, post: bridgeStateId } : activity
      );
      pre = bridgeStateId;
      post = endState.id;
      return bump({
        ...next,
        states,
        activities: [
          ...activitiesRebound,
          {
            id: payload.id || nanoid(),
            roleId: thread.roleId,
            threadId,
            kind: normalizeActionKind(payload.type, payload.className),
            label: payload.label,
            pre,
            post,
          },
        ],
      });
    }
  }

  return bump({
    ...next,
    activities: [
      ...next.activities,
      {
        id: payload.id || nanoid(),
        roleId: thread.roleId,
        threadId,
        kind: normalizeActionKind(payload.type, payload.className),
        label: payload.label,
        pre,
        post,
      },
    ],
  });
};

export const addActivityToRoleVnext = (rad, payload, roleId) => {
  const next = addThreadVnext(rad, {
    role_id: roleId,
    label: payload.threadLabel,
  });
  const createdThread = next.threads[next.threads.length - 1];
  return addActivityToThreadVnext(
    next,
    {
      id: payload.id,
      className: payload.className,
      type: payload.type,
      label: payload.label,
      prestate_id: payload.prestate_id,
      poststate_id: payload.poststate_id,
    },
    createdThread.id
  );
};

export const updateActivityVnext = (rad, patch) => {
  const next = ensureVnextArrays(rad);
  const idx = next.activities.findIndex((activity) => activity.id === patch.id);
  if (idx === -1) throw new Error(`activity ${patch.id} not found`);
  const activities = [...next.activities];
  activities[idx] = {
    ...activities[idx],
    ...(patch.label !== undefined ? { label: patch.label } : {}),
    ...(patch.prestate_id ? { pre: patch.prestate_id } : {}),
    ...(patch.poststate_id ? { post: patch.poststate_id } : {}),
    ...(patch.hidden !== undefined ? { hidden: patch.hidden } : {}),
  };
  return bump({ ...next, activities });
};

export const addConnectionVnext = (rad, interactions, options = {}) => {
  const next = ensureVnextArrays(rad);
  if (interactions.length < 2) throw new Error("at least two interactions are required");
  const uniqueInteractions = Array.from(new Set(interactions));
  const existing = next.connections.find((connection) =>
    connection.interactionIds.some((id) => uniqueInteractions.includes(id))
  );
  if (existing) {
    return bump({
      ...next,
      connections: next.connections.map((connection) =>
        connection.id === existing.id
          ? {
              ...connection,
              interactionIds: Array.from(
                new Set([...connection.interactionIds, ...uniqueInteractions])
              ),
            }
          : connection
      ),
    });
  }
  return bump({
    ...next,
    connections: [
      ...next.connections,
      {
        id: options.id || nanoid(),
        interactionIds: uniqueInteractions,
      },
    ],
  });
};

export const addInteractionVnext = (rad, payload) => {
  const next = ensureVnextArrays(rad);
  const sourceId = payload?.sourceActivityId || payload?.sourceActionId;
  const targetId = payload?.targetActivityId || payload?.targetActionId;
  if (!sourceId || !targetId) {
    throw new Error("sourceActivityId and targetActivityId are required");
  }
  const source = next.activities.find((activity) => activity.id === sourceId);
  const target = next.activities.find((activity) => activity.id === targetId);
  if (!source || !target) {
    throw new Error("interaction source and target activities must exist");
  }
  if (source.kind !== "interaction" || target.kind !== "interaction") {
    throw new Error("source and target activities must be interactions");
  }
  if (source.roleId === target.roleId) {
    throw new Error("interactions must be across distinct roles");
  }
  return addConnectionVnext(
    next,
    [source.id, target.id],
    payload?.connectionId ? { id: payload.connectionId } : {}
  );
};

export const deleteStateVnext = (rad, stateId) => {
  const next = ensureVnextArrays(rad);
  return bump({
    ...next,
    states: next.states.filter((state) => state.id !== stateId),
    activities: next.activities.filter((activity) => activity.pre !== stateId && activity.post !== stateId),
  });
};

export const deleteEntityVnext = (rad, payload) => {
  const next = ensureVnextArrays(rad);
  if (payload.type === "role") {
    const roleId = payload.id;
    const threads = next.threads.filter((thread) => thread.roleId !== roleId);
    const threadIds = new Set(threads.map((thread) => thread.id));
    const states = next.states.filter((state) => state.roleId !== roleId);
    const activities = next.activities.filter((activity) => activity.roleId !== roleId);
    const activityIds = new Set(activities.map((activity) => activity.id));
    return bump({
      ...next,
      roles: next.roles.filter((role) => role.id !== roleId),
      threads,
      states: states.filter((state) => threadIds.has(state.threadId)),
      activities,
      connections: next.connections
        .map((connection) => ({
          ...connection,
          interactionIds: connection.interactionIds.filter((id) => activityIds.has(id)),
        }))
        .filter((connection) => connection.interactionIds.length >= 2),
    });
  }
  if (["thread", "case", "part", "repeat"].includes(payload.type)) {
    const threadId = payload.id;
    const states = next.states.filter((state) => state.threadId !== threadId);
    const activities = next.activities.filter((activity) => activity.threadId !== threadId);
    const activityIds = new Set(activities.map((activity) => activity.id));
    return bump({
      ...next,
      threads: next.threads.filter((thread) => thread.id !== threadId),
      states,
      activities,
      connections: next.connections
        .map((connection) => ({
          ...connection,
          interactionIds: connection.interactionIds.filter((id) => activityIds.has(id)),
        }))
        .filter((connection) => connection.interactionIds.length >= 2),
    });
  }
  if (["activity", "refinement"].includes(payload.type)) {
    const activityId = payload.id;
    const activities = next.activities.filter((activity) => activity.id !== activityId);
    const activityIds = new Set(activities.map((activity) => activity.id));
    return bump({
      ...next,
      activities,
      connections: next.connections
        .map((connection) => ({
          ...connection,
          interactionIds: connection.interactionIds.filter((id) => activityIds.has(id)),
        }))
        .filter((connection) => connection.interactionIds.length >= 2),
    });
  }
  throw new Error(`unsupported delete type ${payload.type}`);
};
